import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";

// 186: shared mobile chrome for list screens (events / players / calendar /
// discover). All pieces are `md:hidden` -- the desktop UI is untouched.

/**
 * Floating action button, bottom-right above the tab bar. Managers only -- the
 * caller decides whether to render it. Pass `to` for a navigation FAB or
 * `onClick` for an in-place action.
 */
export function MobileFab({
  to,
  onClick,
  label,
  icon,
}: {
  to?: string;
  onClick?: () => void;
  label: string;
  icon?: ReactNode;
}) {
  const className =
    "fixed bottom-20 right-4 z-30 flex h-14 w-14 items-center justify-center rounded-[18px] bg-primary text-primary-foreground shadow-lg transition-transform active:scale-95 md:hidden";
  const content = icon ?? <Plus className="h-6 w-6" />;
  return to ? (
    <Link to={to} aria-label={label} data-action="fab" className={className}>
      {content}
    </Link>
  ) : (
    <button type="button" aria-label={label} data-action="fab" onClick={onClick} className={className}>
      {content}
    </button>
  );
}

/** A horizontal-scroll row of filter chips (edge-bleeds inside a `p-4` page). */
export function FilterChipRow({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${className}`}
      data-region="mobile-filters"
    >
      {children}
    </div>
  );
}

/** A single pill filter chip. */
export function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`shrink-0 rounded-full border px-3.5 py-1.5 text-[13px] font-semibold transition-colors ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background text-muted-foreground"
      }`}
    >
      {children}
    </button>
  );
}
